import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import AppShell from '@/components/AppShell';
import ProduceCard from '@/components/ProduceCard';
import { Bookmark } from 'lucide-react';
import { toast } from 'sonner';

export default function SavedPage() {
  const { api } = useAuth();
  const [posts, setPosts] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const fetchSaved = useCallback(async () => {
    try {
      const { data } = await api.get('/saved');
      setPosts(data.posts || []);
      setSavedIds(new Set((data.posts || []).map(p => p.id)));
    } catch {
      toast.error('Failed to load saved items');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchSaved();
  }, [fetchSaved]);

  const handleToggleSave = async (postId) => {
    try {
      const { data } = await api.post(`/saved/${postId}`);
      if (!data.saved) {
        setPosts(prev => prev.filter(p => p.id !== postId));
        setSavedIds(prev => {
          const next = new Set(prev);
          next.delete(postId);
          return next;
        });
        toast.success('Removed from saved');
      }
    } catch {
      toast.error('Failed to update');
    }
  };

  return (
    <AppShell title="Saved">
      <div className="p-4 space-y-4" data-testid="saved-page">
        {loading ? (
          Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] overflow-hidden animate-pulse">
              <div className="w-full h-48 bg-[#EFF2F7]" />
              <div className="p-4 space-y-3">
                <div className="h-4 bg-[#EFF2F7] rounded w-3/4" />
                <div className="h-3 bg-[#EFF2F7] rounded w-full" />
              </div>
            </div>
          ))
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <Bookmark className="w-12 h-12 mx-auto text-[#5B6675]/30 mb-3" />
            <p className="text-[#5B6675] text-sm">No saved produce yet</p>
            <p className="text-xs text-[#5B6675]/60 mt-1">Save items from the feed to view them here</p>
          </div>
        ) : (
          posts.map((post, i) => (
            <ProduceCard
              key={post.id}
              post={post}
              index={i}
              isSaved={savedIds.has(post.id)}
              onToggleSave={handleToggleSave}
            />
          ))
        )}
      </div>
    </AppShell>
  );
}
